import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { partnerDetailsSliceAction } from "../../slices/partner-details";
import {
  partnerDetailsSagaActionTypes,
  getAllPartnersSagaAction,
  getPartnerDetailsSagaAction,
  SaveReportDataSagaActionInput,
  ExportReportSagaActionInput,
} from "./saga-actions";
import { AddPartnerDetailsType, LinkPartnerPayloadType, UpdatePartnerDetailsType, getFilteredPartnersSagaActionPayloadType } from "../../slices/partner-details/Types";

type PartnerDetailsType = {
  ID: number;
  metadata: {
    reason: string;
    history: {
      date: string;
      message: string;
      initiatedBy: string;
    }[];
  };
  partner_id: string;
  parent_partner: {
    ID: number;
    partner_id: string;
    partner_name: string;
    partner_email: string;
    status: string;
  };
  partner_name: string;
  description: string;
  account_manager: string;
  partner_type: string;
  partner_potential: string;
  status: string;
  enable: number;
  partner_email: string;
  website: string;
  region: string;
  revenue_stream: string;
  country: string;
  state: string;
  company_contact: string;
  street_address: string;
  company_logo: string;
  service_tier: string;
  registration_date: string;
  last_updated: string;
};
type getPartnersListAPIResponseType = {
  success: boolean;
  message: string;
  data: PartnerDetailsType[];
};
const getAllPartnersAPI = async () => {
  let response = axiosRestInstance
    .post<AxiosResponse<getPartnersListAPIResponseType>>(
      "/partnerDetails/getAllPartners"
    )
    .then((res) => res.data);
  return response;
};

const getPartnerDropdownAPI = async () => {
  let response = axiosRestInstance
    .post<AxiosResponse<getPartnersListAPIResponseType>>(
      "/partnerDetails/getAllPartners?partner_dropdown=true"
    )
    .then((res) => res.data);
  return response;
};

type getPartnerDetailsAPIResponseType = {
  success: boolean;
  message: string;
  data: PartnerDetailsType;
};
const getPartnerDetailsAPI = async (ID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getPartnerDetailsAPIResponseType>>(
      "/partnerDetails/getPartner/" + ID
    )
    .then((res) => res.data);
  return response;
};

type addPartnerAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const addPartnerAPI = async (partnerDetails: AddPartnerDetailsType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<addPartnerAPIResponseType>>(
      "/partnerDetails/insertPartner",
      partnerDetails
    )
    .then((res) => res.data);
  return response;
};

type updatePartnerAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const updatePartnerAPI = async (partnerDetails: UpdatePartnerDetailsType) => {
  let response = axiosRestInstance
    .put<AxiosResponse<updatePartnerAPIResponseType>>(
      "/partnerDetails/updatePartner/" + partnerDetails.ID,
      partnerDetails
    )
    .then((res) => res.data);
  return response;
};

type getPartnerTypesResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
  }[];
};

const getPartnerTypesAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getPartnerTypesResponseType>>(
      "/lookupData/getlookupData/partnerType"
    )
    .then((res) => res.data);
  return response;
};

type getUserRoleResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
    metadata: {
      hid_users_roles: string[];
      partner_users_roles: string[];
    };
    description: string | null;
  }[];
};

export const getUserRoleAPI = async (): Promise<getUserRoleResponseType> => {
  const response = await axiosRestInstance.get<getUserRoleResponseType>(
    "/lookupData/getlookupData/user_role"
  );
  return response.data;
};

export type columnFilterLabelType = {
  label: string;
  value: string;
  alias?: string;
}

export type getAllJoinedTablesFieldsResponseDataType = {
  [tableName: string]: columnFilterLabelType[];
}

export type getAllJoinedTablesFieldsResponseType = {
  success: boolean;
  message?: string;
  data: getAllJoinedTablesFieldsResponseDataType;
};

export const getJoinedTablesFieldsAPI = async (): Promise<getAllJoinedTablesFieldsResponseType> => {
  const response =
    await axiosRestInstance.get<getAllJoinedTablesFieldsResponseType>(
      "/filter/column"
    );

  return response.data as getAllJoinedTablesFieldsResponseType;
};

type postJoinedTablesFieldsDataTypeResponse = {
  success: boolean;
  message: string;
  data: {
    table: ContractRow[];
    chart: ChartData;
  };
};
type saveReportDataTypeResponse = {
  success: boolean;
  report_id: number;
  mode?: "created" | "updated" | "copy_created";
  // message: string;
};

type ContractRow = Record<string, string | number | boolean>;

type ChartData = {
  title: string;
  type: string; // as per payload
  dimension: DimensionPoint[];
  metrics: MetricSeries[];
};

type DimensionPoint = {
  key: string;   // e.g., "2025-12" (YYYY-MM)
  label: string; // e.g., "Dec"
};

type MetricSeries = {
  name: string;     // e.g., "Record Count"
  values: number[]; // counts per dimension index
};

// Type for the given payload (with precise literals for what's shown)
export type postJoinedTablesFieldsDataType = {
  report_title: string;
  selectColumns: string[];
  filters: Array<{
    field: string;
    operator: "=" | "!=" | ">" | "<" | "IN" | "NOT IN";
    value: string | string[];
  }>;
  chart: {
    type: string;
    title: string;
    xAxis: string;
    yAxis: string;
    dateField: string,
    dateRange: {
      from: string;
      to: string;
    };
  };
};

export type SaveReportDefinitionType = {
  selectColumns: string[];
  filters: Array<{
    field: string;
    operator: "=" | "!=" | ">" | "<" | "IN" | "NOT IN";
    value: string | string[];
  }>;
  chart: {
    type: string;
    title: string;
    xAxis: string;
    yAxis: string;
    dateField: string;
    dateRange: {
      from: string;
      to: string;
    };
  };
};

export type SaveReportPayload = {
  report_id?: number;
  report_mode?: "LIVE" | "SNAPSHOT";
  report_title: string;
  user: number;
  category_id: number;
  definition: SaveReportDefinitionType;
};

export type ExportReportPayload = {
  userID: number;
}

export const postJoinedTablesFieldsDataAPI = async (payload: any): Promise<postJoinedTablesFieldsDataTypeResponse> => {
  let response = await axiosRestInstance
    .post<postJoinedTablesFieldsDataTypeResponse>("/report/run", payload);

  return response.data;
};

export const saveReportDataAPI = async (payload: any): Promise<saveReportDataTypeResponse> => {
  let response = await axiosRestInstance
    .post<saveReportDataTypeResponse>("/report/save", payload);

  return response.data;
};



export const exportReportAPI = async (reportId: number, userId: number): Promise<AxiosResponse<Blob>> => {
  let response = await axiosRestInstance.post(
    `/report/export/${reportId}`,
    { user_id: userId },
    { responseType: "blob" }
  );
  return response;
};



type getRevenueStreamResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
  }[];
};
const getRevenueStreamAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getRevenueStreamResponseType>>(
      "/lookupData/getlookupData/revenueStream"
    )
    .then((res) => res.data);
  return response;
};

type getPartnerVerticalResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
  }[];
};
const getPartnerVerticalAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getPartnerVerticalResponseType>>(
      "/lookupData/getlookupData/partnerVertical"
    )
    .then((res) => res.data);
  return response;
};

type getPartnerTierResponseType = {
  success: boolean;
  message: string;
  data: {
    value: string;
  }[];
};
const getPartnerTierAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getPartnerTierResponseType>>(
      "/lookupData/getlookupData/partnerTier"
    )
    .then((res) => res.data);
  return response;
};

type linkPartnerResponseType = {
  success: boolean;
  message: string;
};
const linkPartnerAPI = async (payload: LinkPartnerPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<linkPartnerResponseType>>(
      "/partnerLink/linkPartner",
      payload
    )
    .then((res) => res.data);
  return response;
};

type unlinkPartnerResponseType = {
  success: boolean;
  message: string;
};
const unlinkPartnerAPI = async (ID: number) => {
  let response = axiosRestInstance
    .put<AxiosResponse<unlinkPartnerResponseType>>("/partnerLink/unlink/" + ID)
    .then((res) => res.data);
  return response;
};

type getPartnerChildrenResponseType = {
  success: boolean;
  message: string;
  parent_partner: {
    ID: number;
    partner_id: string;
    partner_name: string;
    partner_email: string;
    status: string;
  };
  child_partners: {
    ID: number;
    partner_id: string;
    partner_name: string;
    partner_email: string;
    status: string;
  }[];
};
const getPartnerChildrenAPI = async (ID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getPartnerChildrenResponseType>>("/partnerChild/" + ID)
    .then((res) => res.data);
  return response;
};

type togglePartnerEnableResponseType = {
  success: boolean;
  message: string;
};
const togglePartnerEnableAPI = async (payload: {
  ID: number;
  enable: number;
}) => {
  let response = axiosRestInstance
    .put<AxiosResponse<togglePartnerEnableResponseType>>(
      "/partnerDetails/partnerStatus/" + payload.ID,
      { enable: payload.enable }
    )
    .then((res) => res.data);
  return response;
};

const getFilteredPartnersAPI = async (
  payload: getFilteredPartnersSagaActionPayloadType
) => {
  let response = axiosRestInstance
    .post<AxiosResponse<getPartnersListAPIResponseType>>(
      "/partnerDetails/getAllPartners",
      payload
    )
    .then((res) => res.data);
  return response;
};

type deletePartnerResponseType = {
  success: boolean;
  message: string;
};
const deletePartnerAPI = async (ID: number) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<deletePartnerResponseType>>("/partnerDetails/" + ID)
    .then((res) => res.data);
  return response;
};

// ----------------------- API Above

function* getAllPartnersSaga(action: { type: string }) {
  try {
    yield put(partnerDetailsSliceAction.setAllPartnersLoading(true));
    let response: getPartnersListAPIResponseType = yield call(
      getAllPartnersAPI
    );
    let allPartnersArray = response.data.map((partner) => ({
      ...partner,
      partner_type: partner.partner_type.split(","),
    }));
    yield put(
      partnerDetailsSliceAction.setAllPartners(allPartnersArray.reverse())
    );
    yield put(partnerDetailsSliceAction.setAllPartnersLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setAllPartnersLoading(false));
    console.log(e);
  }
}

function* getPartnerDropdownSaga(action: { type: string }) {
  try {
    yield put(partnerDetailsSliceAction.setPartnerDropdownLoading(true));
    let response: getPartnersListAPIResponseType = yield call(
      getPartnerDropdownAPI
    );
    const options = (response.data ?? []).map((partner) => ({
      label: partner.partner_name,
      value: partner.ID,
    }));
    yield put(partnerDetailsSliceAction.setPartnerDropdownOptions(options));
    yield put(partnerDetailsSliceAction.setPartnerDropdownLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setPartnerDropdownOptions([]));
    yield put(partnerDetailsSliceAction.setPartnerDropdownLoading(false));
    console.log(e);
  }
}

function* getPartnerDetailsSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setPartnerDetailsLoading(true));
    let response: getPartnerDetailsAPIResponseType = yield call(
      getPartnerDetailsAPI,
      action.payload.ID
    );
    const partnerDetails = {
      ...response.data,
      partner_type: response.data.partner_type.split(","),
    };
    yield put(partnerDetailsSliceAction.setPartnerDetails(partnerDetails));
    yield put(partnerDetailsSliceAction.setPartnerDetailsLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setPartnerDetails(null));
    yield put(partnerDetailsSliceAction.setPartnerDetailsLoading(false));
    console.log(e);
  }
}

function* addPartnerSaga(action: {
  type: string;
  payload: {
    partnerDetails: AddPartnerDetailsType;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setAddPartnerLoading(true));
    let response: addPartnerAPIResponseType = yield call(
      addPartnerAPI,
      action.payload.partnerDetails
    );
    yield put(partnerDetailsSliceAction.setAddPartnerStatus(response.success));
    yield put(partnerDetailsSliceAction.setAddPartnerMessage(""));
    yield put(partnerDetailsSliceAction.setAddPartnerLoading(false));
    yield put(getAllPartnersSagaAction());
  } catch (e: any) {
    yield put(partnerDetailsSliceAction.setAddPartnerStatus(false));
    if (e.response?.data?.response?.code === "ER_DUP_ENTRY") {
      yield put(
        partnerDetailsSliceAction.setAddPartnerMessage(
          "Duplicate Partner Name!"
        )
      );
    } else {
      yield put(
        partnerDetailsSliceAction.setAddPartnerMessage("Something went wrong!")
      );
    }
    yield put(partnerDetailsSliceAction.setAddPartnerLoading(false));
    console.log(e);
  }
}

function* updatePartnerSaga(action: {
  type: string;
  payload: {
    partnerDetails: UpdatePartnerDetailsType;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setUpdatePartnerLoading(true));
    let response: updatePartnerAPIResponseType = yield call(
      updatePartnerAPI,
      action.payload.partnerDetails
    );
    yield put(
      partnerDetailsSliceAction.setUpdatePartnerMessage(response.message)
    );
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(true));
    if (!action.payload.partnerDetails.togglePartnerStatus)
      yield put(getPartnerDetailsSagaAction(action.payload.partnerDetails.ID));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(false));
    console.log(e);
  } finally {
    yield put(partnerDetailsSliceAction.setUpdatePartnerLoading(false));
  }
}

function* getPartnerTypesSaga(action: { type: string }) {
  try {
    let response: getPartnerTypesResponseType = yield call(getPartnerTypesAPI);
    yield put(partnerDetailsSliceAction.setPartnerTypes(response.data));
  } catch (e) {
    console.log(e);
  }
}

function* getRevenueStreamSaga(action: { type: string }) {
  try {
    let response: getRevenueStreamResponseType = yield call(
      getRevenueStreamAPI
    );
    yield put(partnerDetailsSliceAction.setRevenueStream(response.data));
  } catch (e) {
    console.log(e);
  }
}

function* getPartnerVerticalSaga(action: { type: string }) {
  try {
    let response: getPartnerVerticalResponseType = yield call(
      getPartnerVerticalAPI
    );
    yield put(partnerDetailsSliceAction.setPartnerVertical(response.data));
  } catch (e) {
    console.log(e);
  }
}

function* getPartnerTierSaga(action: { type: string }) {
  try {
    let response: getPartnerTierResponseType = yield call(getPartnerTierAPI);
    yield put(partnerDetailsSliceAction.setPartnerTier(response.data));
  } catch (e) {
    console.log(e);
  }
}

function* linkPartnerSaga(action: {
  type: string;
  payload: LinkPartnerPayloadType;
}) {
  try {
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(true));
    let response: linkPartnerResponseType = yield call(
      linkPartnerAPI,
      action.payload
    );
    yield put(partnerDetailsSliceAction.setLinkPartnerStatus(true));
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setLinkPartnerStatus(false));
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(false));
    console.log(e);
  }
}

function* unlinkPartnerSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(true));
    let response: linkPartnerResponseType = yield call(
      unlinkPartnerAPI,
      action.payload.ID
    );
    yield put(partnerDetailsSliceAction.setLinkPartnerStatus(true));
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setLinkPartnerStatus(false));
    yield put(partnerDetailsSliceAction.setLinkPartnerLoading(false));
    console.log(e);
  }
}

function* getPartnerChildrenSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setPartnerChildrenLoading(true));
    let response: getPartnerChildrenResponseType = yield call(
      getPartnerChildrenAPI,
      action.payload.ID
    );
    yield put(
      partnerDetailsSliceAction.setPartnerChildren(
        response.child_partners || []
      )
    );
    yield put(partnerDetailsSliceAction.setPartnerChildrenLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setPartnerChildren([]));
    yield put(partnerDetailsSliceAction.setPartnerChildrenLoading(false));
    console.log(e);
  }
}

function* togglePartnerEnableSaga(action: {
  type: string;
  payload: {
    ID: number;
    enable: number;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setTogglePartnerEnableLoading(true));
    let response: togglePartnerEnableResponseType = yield call(
      togglePartnerEnableAPI,
      action.payload
    );
    yield put(partnerDetailsSliceAction.setTogglePartnerEnableStatus(true));
    yield put(partnerDetailsSliceAction.setTogglePartnerEnableLoading(false));
  } catch (e) {
    yield put(partnerDetailsSliceAction.setTogglePartnerEnableStatus(false));
    yield put(partnerDetailsSliceAction.setTogglePartnerEnableLoading(false));
    console.log(e);
  }
}

function* getFilteredPartnersSaga(action: {
  type: string;
  payload: getFilteredPartnersSagaActionPayloadType;
}) {
  try {
    yield put(partnerDetailsSliceAction.setFilteredPartnersLoading(true));
    let response: getPartnersListAPIResponseType = yield call(
      getFilteredPartnersAPI,
      action.payload
    );
    let filteredPartnersArray = response.data.map((partner) => ({
      ...partner,
      partner_type: partner.partner_type.split(","),
    }));
    yield put(
      partnerDetailsSliceAction.setFilteredPartners(
        filteredPartnersArray.reverse()
      )
    );
  } catch (e) {
    yield put(partnerDetailsSliceAction.setFilteredPartners([]));
    console.log(e);
  } finally {
    yield put(partnerDetailsSliceAction.setFilteredPartnersLoading(false));
  }
}

function* deletePartnerSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(partnerDetailsSliceAction.setDeletePartnerLoading(true));
    let response: deletePartnerResponseType = yield call(
      deletePartnerAPI,
      action.payload.ID
    );
    yield put(partnerDetailsSliceAction.setDeletePartnerStatus(true));
  } catch (e: any) {
    yield put(partnerDetailsSliceAction.setDeletePartnerStatus(false));
    yield put(
      partnerDetailsSliceAction.setDeletePartnerMessage(
        e?.response?.data?.message || "Something went wrong"
      )
    );
    console.log(e);
  } finally {
    yield put(partnerDetailsSliceAction.setDeletePartnerLoading(false));
  }
}

// --------------------- Add these types here ---------------------
type RoleMetadataType = {
  hid_users_roles: string[];
  partner_users_roles: string[];
};

type GetUserRoleAPIResponse = AxiosResponse<{
  success: boolean;
  message: string;
  data: {
    value: string;
    metadata: RoleMetadataType;
    description: string | null;
  }[];
}>;
// -----------------------------------------------------------------

function* getUserRolesSaga() {
  try {
    yield put(partnerDetailsSliceAction.setUserRolesLoading(true));

    const response: getUserRoleResponseType = yield call(getUserRoleAPI);
    console.log("API response:", response);

    const metadata = response?.data?.[0]?.metadata ?? { hid_users_roles: [], partner_users_roles: [] };

    yield put(
      partnerDetailsSliceAction.setUserRoles({
        hidRoles: Array.isArray(metadata.hid_users_roles) ? metadata.hid_users_roles : [],
        partnerRoles: Array.isArray(metadata.partner_users_roles) ? metadata.partner_users_roles : [],
      })
    );
    yield put(partnerDetailsSliceAction.setUserRolesError(false)); // clear error
  } catch (err) {
    console.error("Saga error:", err);
    yield put(partnerDetailsSliceAction.setUserRolesError(true));
    yield put(partnerDetailsSliceAction.setUserRoles({ hidRoles: [], partnerRoles: [] })); // always set empty arrays
  } finally {
    yield put(partnerDetailsSliceAction.setUserRolesLoading(false));
  }
}

const formatTableName = (tableName: string) => {
  return tableName
    .replace(/_/g, " ")          // snake_case → space
    .split(" ")                 // split words
    .map(
      word => word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
};

function prepareResponse(response: getAllJoinedTablesFieldsResponseDataType): columnFilterLabelType[] {
  const resultArray = Object.entries(response).flatMap(
    ([tableName, fields]) => {
      const formattedTableName = formatTableName(tableName);

      return fields.map(field => ({
        label: `${field.label}`,
        value: field.value,
        alias: field.alias,
      }));
    }
  );

  return resultArray
}

function* getJoinedTablesFieldsSaga(action: { type: string }) {
  try {
    yield put(partnerDetailsSliceAction.setJoinedTablesFieldsLoading(true));

    const response: getAllJoinedTablesFieldsResponseType = yield call(
      getJoinedTablesFieldsAPI
    );
    const resultArray: columnFilterLabelType[] = prepareResponse(response.data);

    yield put(
      partnerDetailsSliceAction.setJoinedTablesFields(resultArray as columnFilterLabelType[])
    );
  } catch (err) {
    console.error("Saga error:", err);
    yield put(partnerDetailsSliceAction.setJoinedTablesFieldsError(true));
    yield put(partnerDetailsSliceAction.setJoinedTablesFields({}));
  } finally {
    yield put(partnerDetailsSliceAction.setJoinedTablesFieldsLoading(false));
  }
}

function* handleSaveReportDataSaga(action: { type: string; data: SaveReportDataSagaActionInput }) {
  const { payload, onSuccess, onError } = action.data;
  try {
    const response: saveReportDataTypeResponse = yield call(saveReportDataAPI, payload);
    if (!response?.success) {
      yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(false));
      const msg = (response as any)?.message || "Unable to save report.";
      yield put(partnerDetailsSliceAction.setUpdatePartnerMessage(msg));
      onError?.(msg);
      return;
    }
    const msg =
      response.mode === "updated"
        ? "Report updated successfully."
        : response.mode === "copy_created"
          ? "Report copy saved successfully."
          : "Report saved successfully.";

    yield put(partnerDetailsSliceAction.setsaveReportDataAction(payload));
    yield put(partnerDetailsSliceAction.setLastSavedReportId(response.report_id));
    yield put(partnerDetailsSliceAction.setUpdatePartnerMessage(msg));
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(true));
    onSuccess?.({ report_id: response.report_id, mode: response.mode });
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      "Network or server error while saving report.";
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(false));
    yield put(partnerDetailsSliceAction.setUpdatePartnerMessage(msg));
    onError?.(msg);
  }
}

const getFileNameFromHeaders = (
  headers: Record<string, any>,
  fallback = "hid-dashboard-report.xlsx"
) => {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!cd) return fallback;
  const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i.exec(cd);
  if (!match) return fallback;
  const raw = match[1].replace(/['"]/g, "");
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw || fallback;
  }
};


function* handleExportReportSaga(action: { type: string; payload: ExportReportSagaActionInput }) {
  const { reportId, userId, fileName } = action.payload;

  try {
    yield put(partnerDetailsSliceAction.setExportReportLoading(true));

    const response: AxiosResponse<Blob> = yield call(exportReportAPI, reportId, userId);
    const blob: Blob = response?.data;
    if (!blob || !(blob instanceof Blob)) {
      throw new Error("Invalid export response.");
    }
    const inferredName = getFileNameFromHeaders(response.headers, "hid-dashboard-report.xlsx");
    const finalName = fileName || inferredName;
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = finalName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    // yield put(partnerDetailsSliceAction.setUpdatePartnerMessage("Report exported successfully."));
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(true));
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Unable to export report.";
    yield put(partnerDetailsSliceAction.setUpdatePartnerMessage(msg));
    yield put(partnerDetailsSliceAction.setUpdatePartnerStatus(false));
  } finally {
    yield put(partnerDetailsSliceAction.setExportReportLoading(false));
  }
}



function* partnerDetailsSaga() {
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetAllPartnersSaga,
    getAllPartnersSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerDropdownSaga,
    getPartnerDropdownSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerDetailsSaga,
    getPartnerDetailsSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callAddPartnerSaga,
    addPartnerSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callUpdatePartnerSaga,
    updatePartnerSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerTypesSaga,
    getPartnerTypesSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetRevenueStreamSaga,
    getRevenueStreamSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerVerticalSaga,
    getPartnerVerticalSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerTierSaga,
    getPartnerTierSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callLinkPartnerSaga,
    linkPartnerSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callUnlinkPartnerSaga,
    unlinkPartnerSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetPartnerChildrenSaga,
    getPartnerChildrenSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callTogglePartnerEnableSaga,
    togglePartnerEnableSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetFilteredPartnersSaga,
    getFilteredPartnersSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callDeletePartnerSaga,
    deletePartnerSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetUserRolesSaga,
    getUserRolesSaga
  );
  yield takeLatest(
    partnerDetailsSagaActionTypes.callGetJoinedTablesFieldsSaga,
    getJoinedTablesFieldsSaga
  );
  yield takeLatest(partnerDetailsSagaActionTypes.callSaveReportDataSaga, handleSaveReportDataSaga);

  yield takeLatest(
    partnerDetailsSagaActionTypes.callExportReportSaga,
    handleExportReportSaga
  );

}

export default partnerDetailsSaga;
